import React, { useState, useEffect, useCallback, useRef } from 'react';
import { RefreshCw, Eye, ChevronDown, CheckCircle, Clock, XCircle, AlertCircle, Layers, TrendingUp, BarChart3, Lock, Filter, Calendar, Package } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import config from '@/config';

function WorkflowAnalyticsDashboard() {
  const { authenticatedFetch } = useAuth();
  
  const [stats, setStats] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [nodes, setNodes] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, page: 1, page_size: 20, total_pages: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Workflow Items (Node Types)
  const [workflowItems, setWorkflowItems] = useState([]);
  const [workflowItemsLoading, setWorkflowItemsLoading] = useState(false);
  
  // Filters
  const [workflowId, setWorkflowId] = useState('');
  const [workflowItemId, setWorkflowItemId] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [createdAfter, setCreatedAfter] = useState('');
  const [createdBefore, setCreatedBefore] = useState('');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const [currentPage, setCurrentPage] = useState(1);
  
  const [showFilters, setShowFilters] = useState(false);
  const [selectedNodeType, setSelectedNodeType] = useState('all');
  const [selectedTimelineMetric, setSelectedTimelineMetric] = useState('status');
  
  const refreshIntervalRef = useRef(null);

  // Fetch workflow items on mount
  useEffect(() => {
    fetchWorkflowItems();
  }, []);

  const fetchWorkflowItems = async () => {
    setWorkflowItemsLoading(true);
    try {
      const response = await authenticatedFetch(
        `${config.BACKEND_API_ADDRESS}/nodes/workflow-items/list`
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setWorkflowItems(data);
      
    } catch (err) {
      console.error('Error fetching workflow items:', err);
    } finally {
      setWorkflowItemsLoading(false);
    }
  };

  const fetchAnalytics = useCallback(async (page = currentPage) => {
    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams({
        page: page.toString(),
        page_size: '20',
        sort_by: sortBy,
        sort_order: sortOrder
      });
      
      if (workflowId) params.append('workflow_id', workflowId);
      if (workflowItemId) params.append('workflow_item_id', workflowItemId);
      if (statusFilter) params.append('status', statusFilter);
      if (createdAfter) params.append('created_after', createdAfter);
      if (createdBefore) params.append('created_before', createdBefore);

      const response = await authenticatedFetch(
        `${config.BACKEND_API_ADDRESS}/nodes/workflow-nodes/analytics?${params.toString()}`
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      setStats(data.stats);
      setTimeline(data.timeline);
      setNodes(data.nodes);
      setPagination(data.pagination);
      setCurrentPage(page);
      
    } catch (err) {
      console.error('Error fetching analytics:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [authenticatedFetch, workflowId, workflowItemId, statusFilter, createdAfter, createdBefore, sortBy, sortOrder, currentPage]);

  useEffect(() => {
    fetchAnalytics(1);
  }, [statusFilter, workflowItemId, sortBy, sortOrder]);

  useEffect(() => {
    refreshIntervalRef.current = setInterval(() => {
      fetchAnalytics(currentPage);
    }, 10000);

    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
    };
  }, [fetchAnalytics, currentPage]);

  const handleRefresh = () => {
    fetchAnalytics(1);
  };

  const handleApplyFilters = () => {
    setCurrentPage(1);
    fetchAnalytics(1);
  };

  const handleResetFilters = () => {
    setWorkflowId('');
    setWorkflowItemId('');
    setStatusFilter('');
    setCreatedAfter('');
    setCreatedBefore('');
    setSortBy('created_at');
    setSortOrder('desc');
    setCurrentPage(1);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'running': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'completed': return 'bg-green-100 text-green-800 border-green-200';
      case 'failed': return 'bg-red-100 text-red-800 border-red-200';
      case 'needs_update': return 'bg-orange-100 text-orange-800 border-orange-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending': return <Clock className="w-4 h-4" />;
      case 'running': return <RefreshCw className="w-4 h-4 animate-spin" />;
      case 'completed': return <CheckCircle className="w-4 h-4" />;
      case 'failed': return <XCircle className="w-4 h-4" />;
      case 'needs_update': return <AlertCircle className="w-4 h-4" />;
      default: return <AlertCircle className="w-4 h-4" />;
    }
  };

  const renderTimelineChart = () => {
    if (!timeline || timeline.length === 0) return null;

    const maxTotal = Math.max(...timeline.map(bucket => bucket.total));
    const nodeTypes = stats ? Object.keys(stats.node_type_distribution) : [];
    const statuses = ['pending', 'running', 'completed', 'failed', 'needs_update'];

    // Filter timeline by selected node type
    const filteredTimeline = selectedNodeType === 'all' 
      ? timeline 
      : timeline.filter(bucket => bucket.node_type_counts[selectedNodeType] > 0);

    // Get data based on selected metric
    const getMetricData = (bucket) => {
      if (selectedTimelineMetric === 'status') {
        return bucket.status_counts;
      } else {
        return bucket.node_type_counts;
      }
    };

    const getMetricKeys = () => {
      if (selectedTimelineMetric === 'status') {
        return statuses;
      } else {
        return nodeTypes;
      }
    };

    const getMetricColor = (key) => {
      if (selectedTimelineMetric === 'status') {
        switch (key) {
          case 'pending': return '#FCD34D';
          case 'running': return '#60A5FA';
          case 'completed': return '#34D399';
          case 'failed': return '#F87171';
          case 'needs_update': return '#FB923C';
          default: return '#D1D5DB';
        }
      } else {
        const colors = ['#8B5CF6', '#EC4899', '#F59E0B', '#10B981', '#3B82F6', '#EF4444'];
        const index = nodeTypes.indexOf(key);
        return colors[index % colors.length];
      }
    };

    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <TrendingUp className="w-5 h-5 text-indigo-600" />
            <h3 className="text-lg font-semibold text-gray-900">Processing Timeline</h3>
          </div>
          <div className="flex items-center gap-3">
            {/* Node Type Filter */}
            <select
              value={selectedNodeType}
              onChange={(e) => setSelectedNodeType(e.target.value)}
              className="text-sm border border-gray-300 rounded-lg px-3 py-1.5 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            >
              <option value="all">All Node Types</option>
              {nodeTypes.map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
            
            {/* Metric Selector */}
            <select
              value={selectedTimelineMetric}
              onChange={(e) => setSelectedTimelineMetric(e.target.value)}
              className="text-sm border border-gray-300 rounded-lg px-3 py-1.5 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            >
              <option value="status">By Status</option>
              <option value="node_type">By Node Type</option>
            </select>
          </div>
        </div>

        <div className="space-y-2">
          {filteredTimeline.map((bucket, index) => {
            const metricData = getMetricData(bucket);
            const metricKeys = getMetricKeys();
            
            return (
              <div key={index} className="flex items-center gap-3">
                <div className="w-32 text-xs text-gray-600 font-medium">
                  {new Date(bucket.timestamp).toLocaleString('en-US', { 
                    month: 'short', 
                    day: 'numeric', 
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </div>
                
                <div className="flex-1 h-8 bg-gray-100 rounded-lg overflow-hidden flex">
                  {metricKeys.map(key => {
                    const count = metricData[key] || 0;
                    const percentage = bucket.total > 0 ? (count / bucket.total) * 100 : 0;
                    
                    if (count === 0) return null;
                    
                    return (
                      <div
                        key={key}
                        className="flex items-center justify-center text-xs font-medium text-white"
                        style={{
                          width: `${percentage}%`,
                          backgroundColor: getMetricColor(key)
                        }}
                        title={`${key}: ${count}`}
                      >
                        {percentage > 10 && count}
                      </div>
                    );
                  })}
                </div>
                
                <div className="w-12 text-xs text-gray-600 font-medium text-right">
                  {bucket.total}
                </div>
              </div>
            );
          })}
        </div>

        {/* Legend */}
        <div className="mt-6 pt-4 border-t border-gray-200">
          <div className="flex flex-wrap gap-4">
            {getMetricKeys().map(key => {
              const total = selectedTimelineMetric === 'status'
                ? stats?.status_distribution[key] || 0
                : stats?.node_type_distribution[key] || 0;
              
              return (
                <div key={key} className="flex items-center gap-2">
                  <div 
                    className="w-3 h-3 rounded"
                    style={{ backgroundColor: getMetricColor(key) }}
                  />
                  <span className="text-xs text-gray-600">
                    {key}: <span className="font-medium">{total}</span>
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="flex flex-col h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 shadow-sm">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                Workflow Analytics Dashboard
              </h1>
              <p className="text-sm text-gray-600 mt-1">
                {stats && `${stats.total_nodes} total nodes`}
              </p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors text-sm font-medium ${
                  showFilters
                    ? 'bg-indigo-600 text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                <Filter className="w-4 h-4" />
                Filters
              </button>
              <button
                onClick={handleRefresh}
                disabled={loading}
                className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                <span className="text-sm font-medium">Refresh</span>
              </button>
            </div>
          </div>

          {/* Filters Panel */}
          {showFilters && (
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Workflow ID
                  </label>
                  <input
                    type="text"
                    value={workflowId}
                    onChange={(e) => setWorkflowId(e.target.value)}
                    placeholder="UUID..."
                    className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>
                
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Node Type
                  </label>
                  <select
                    value={workflowItemId}
                    onChange={(e) => setWorkflowItemId(e.target.value)}
                    className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    disabled={workflowItemsLoading}
                  >
                    <option value="">All Node Types</option>
                    {workflowItems.map(item => (
                      <option key={item.id} value={item.id}>
                        {item.name || item.identifier}
                        {item.description && ` - ${item.description}`}
                      </option>
                    ))}
                  </select>
                  {workflowItemsLoading && (
                    <p className="text-xs text-gray-500 mt-1">Loading node types...</p>
                  )}
                </div>
                
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Status
                  </label>
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  >
                    <option value="">All Statuses</option>
                    <option value="pending">Pending</option>
                    <option value="running">Running</option>
                    <option value="completed">Completed</option>
                    <option value="failed">Failed</option>
                    <option value="needs_update">Needs Update</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Created After
                  </label>
                  <input
                    type="datetime-local"
                    value={createdAfter}
                    onChange={(e) => setCreatedAfter(e.target.value)}
                    className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>
                
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Created Before
                  </label>
                  <input
                    type="datetime-local"
                    value={createdBefore}
                    onChange={(e) => setCreatedBefore(e.target.value)}
                    className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>
                
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Sort By
                  </label>
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  >
                    <option value="created_at">Created Date</option>
                    <option value="updated_at">Updated Date</option>
                    <option value="status">Status</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Sort Order
                  </label>
                  <select
                    value={sortOrder}
                    onChange={(e) => setSortOrder(e.target.value)}
                    className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  >
                    <option value="desc">Descending</option>
                    <option value="asc">Ascending</option>
                  </select>
                </div>
              </div>
              
              <div className="flex items-center gap-3 mt-4">
                <button
                  onClick={handleApplyFilters}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-sm font-medium"
                >
                  Apply Filters
                </button>
                <button
                  onClick={handleResetFilters}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 text-sm font-medium"
                >
                  Reset
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto">
        <div className="px-6 py-6 space-y-6">
          {error && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-red-800">Error loading analytics</p>
                <p className="text-sm text-red-700">{error}</p>
              </div>
            </div>
          )}

          {/* Stats Cards */}
          {stats && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600 font-medium">Total Nodes</p>
                    <p className="text-3xl font-bold text-gray-900 mt-2">{stats.total_nodes}</p>
                  </div>
                  <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center">
                    <Layers className="w-6 h-6 text-indigo-600" />
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600 font-medium">Success Rate</p>
                    <p className="text-3xl font-bold text-green-600 mt-2">
                      {stats.success_rate ? `${stats.success_rate}%` : 'N/A'}
                    </p>
                  </div>
                  <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                    <CheckCircle className="w-6 h-6 text-green-600" />
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600 font-medium">Avg Processing Time</p>
                    <p className="text-3xl font-bold text-blue-600 mt-2">
                      {stats.avg_processing_time_minutes 
                        ? `${stats.avg_processing_time_minutes.toFixed(1)}m`
                        : 'N/A'}
                    </p>
                  </div>
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                    <Clock className="w-6 h-6 text-blue-600" />
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600 font-medium">Currently Locked</p>
                    <p className="text-3xl font-bold text-orange-600 mt-2">{stats.currently_locked}</p>
                  </div>
                  <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                    <Lock className="w-6 h-6 text-orange-600" />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Status Distribution */}
          {stats && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="flex items-center gap-3 mb-4">
                  <BarChart3 className="w-5 h-5 text-indigo-600" />
                  <h3 className="text-lg font-semibold text-gray-900">Status Distribution</h3>
                </div>
                <div className="space-y-3">
                  {Object.entries(stats.status_distribution).map(([status, count]) => (
                    <div key={status}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-medium text-gray-700 capitalize">{status}</span>
                        <span className="text-sm font-semibold text-gray-900">
                          {count} ({stats.status_percentages[status]}%)
                        </span>
                      </div>
                      <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                        <div
                          className="h-full transition-all duration-500"
                          style={{
                            width: `${stats.status_percentages[status]}%`,
                            backgroundColor: 
                              status === 'completed' ? '#34D399' :
                              status === 'running' ? '#60A5FA' :
                              status === 'pending' ? '#FCD34D' :
                              status === 'failed' ? '#F87171' : '#D1D5DB'
                          }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

<div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="flex items-center gap-3 mb-4">
                  <Package className="w-5 h-5 text-indigo-600" />
                  <h3 className="text-lg font-semibold text-gray-900">Node Type Distribution</h3>
                </div>
                <div className="space-y-3">
                  {Object.entries(stats.node_type_distribution).map(([type, count], index) => {
                    const percentage = (count / stats.total_nodes) * 100;
                    
                    // Get status breakdown for this node type from backend data
                    const statusBreakdown = stats.node_type_status_breakdown?.[type] || {};
                    
                    return (
                      <div key={type}>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm font-medium text-gray-700 truncate">{type}</span>
                          <span className="text-sm font-semibold text-gray-900">
                            {count} ({percentage.toFixed(1)}%)
                          </span>
                        </div>
                        
                        {/* Status breakdown bar compressed to the node type's width */}
                        <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
                          <div 
                            className="h-full flex"
                            style={{ width: `${percentage}%` }}
                          >
                            {statusBreakdown.completed > 0 && (
                              <div
                                className="h-full"
                                style={{
                                  width: `${(statusBreakdown.completed / count) * 100}%`,
                                  backgroundColor: '#34D399'
                                }}
                                title={`Completed: ${statusBreakdown.completed}`}
                              />
                            )}
                            {statusBreakdown.running > 0 && (
                              <div
                                className="h-full"
                                style={{
                                  width: `${(statusBreakdown.running / count) * 100}%`,
                                  backgroundColor: '#60A5FA'
                                }}
                                title={`Running: ${statusBreakdown.running}`}
                              />
                            )}
                            {statusBreakdown.pending > 0 && (
                              <div
                                className="h-full"
                                style={{
                                  width: `${(statusBreakdown.pending / count) * 100}%`,
                                  backgroundColor: '#FCD34D'
                                }}
                                title={`Pending: ${statusBreakdown.pending}`}
                              />
                            )}
                            {statusBreakdown.failed > 0 && (
                              <div
                                className="h-full"
                                style={{
                                  width: `${(statusBreakdown.failed / count) * 100}%`,
                                  backgroundColor: '#F87171'
                                }}
                                title={`Failed: ${statusBreakdown.failed}`}
                              />
                            )}
                            {statusBreakdown.needs_update > 0 && (
                              <div
                                className="h-full"
                                style={{
                                  width: `${(statusBreakdown.needs_update / count) * 100}%`,
                                  backgroundColor: '#FB923C'
                                }}
                                title={`Needs Update: ${statusBreakdown.needs_update}`}
                              />
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* Timeline Chart */}
          {renderTimelineChart()}

          {/* Nodes Table */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Workflow Nodes</h3>
              <p className="text-sm text-gray-600 mt-1">
                Page {pagination.page} of {pagination.total_pages} ({pagination.total} total)
              </p>
            </div>

            {nodes.length === 0 && !loading ? (
              <div className="p-12 text-center">
                <Layers className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600">No nodes found</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Node
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Type
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Created
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Processing Time
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Lock Status
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {nodes.map((node) => (
                      <tr key={node.node_id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-xs font-mono text-gray-900">
                            {node.node_id.slice(0, 8)}...
                          </div>
                          <div className="text-xs text-gray-500">
                            v{node.version}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm text-gray-900">
                            {node.workflow_item_name || node.workflow_item_identifier}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(node.status)}`}>
                            {getStatusIcon(node.status)}
                            {node.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {new Date(node.created_at).toLocaleString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {node.processing_time_minutes 
                            ? `${node.processing_time_minutes.toFixed(1)}m`
                            : '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {node.is_locked ? (
                            <div className="text-xs">
                              <div className="flex items-center gap-1 text-orange-600 font-medium mb-1">
                                <Lock className="w-3 h-3" />
                                Locked
                              </div>
                              {node.lock_worker_id && (
                                <div className="text-gray-600">
                                  Worker: {node.lock_worker_id.slice(0, 8)}
                                </div>
                              )}
                              {node.lock_progress !== null && (
                                <div className="text-gray-600">
                                  Progress: {node.lock_progress}%
                                </div>
                              )}
                            </div>
                          ) : (
                            <span className="text-xs text-gray-500">Unlocked</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pagination */}
            {pagination.total_pages > 1 && (
              <div className="p-4 border-t border-gray-200 flex items-center justify-between">
                <button
                  onClick={() => fetchAnalytics(currentPage - 1)}
                  disabled={currentPage === 1 || loading}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
                >
                  Previous
                </button>
                <span className="text-sm text-gray-600">
                  Page {currentPage} of {pagination.total_pages}
                </span>
                <button
                  onClick={() => fetchAnalytics(currentPage + 1)}
                  disabled={currentPage === pagination.total_pages || loading}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
                >
                  Next
                </button>
              </div>
            )}
          </div>

          {loading && (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="w-6 h-6 text-indigo-600 animate-spin" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default WorkflowAnalyticsDashboard;